import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/** Sobretítulo editorial: traço + rótulo em caixa alta espaçada. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn('flex items-center gap-3 text-[11.5px] font-semibold tracking-[0.22em] text-fg-muted uppercase', className)}>
      <span aria-hidden className="h-px w-7 bg-fg-subtle/70" />{children}
    </p>
  );
}

/** Cabeçalho de página: sobretítulo, título em fonte de destaque, descrição e ações à direita. */
export function PageHeader({ eyebrow, title, description, actions, size = 'md', className }: {
  eyebrow?: ReactNode; title: ReactNode; description?: ReactNode; actions?: ReactNode;
  size?: 'md' | 'lg'; className?: string;
}) {
  return (
    <header className={cn('flex flex-wrap items-end justify-between gap-x-6 gap-y-4', className)}>
      <div className="min-w-0">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className={cn('text-fg text-balance', eyebrow && 'mt-3',
          size === 'lg' ? 'text-[2.6rem] leading-[1.02] sm:text-[3.4rem]' : 'text-[1.9rem] leading-tight sm:text-[2.2rem]')}>
          {title}
        </h1>
        {description && <p className="mt-2.5 max-w-2xl text-[15px] leading-relaxed text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/** Estado vazio: moldura tracejada, título forte, texto e ações. */
export function EmptyState({ title, children, actions, icon, className }: {
  title: ReactNode; children?: ReactNode; actions?: ReactNode; icon?: ReactNode; className?: string;
}) {
  return (
    <div className={cn('rounded-2xl border border-dashed border-border-strong p-7 sm:p-9', className)}>
      {icon && <div className="mb-4 text-fg-subtle">{icon}</div>}
      <p className="text-[1.35rem] leading-snug font-semibold tracking-[-0.02em] text-fg">{title}</p>
      {children && <div className="mt-2 max-w-2xl text-[14.5px] leading-relaxed text-fg-muted">{children}</div>}
      {actions && <div className="mt-6 flex flex-wrap gap-2.5">{actions}</div>}
    </div>
  );
}
